/**
 * Procedural audio for Zombie Usurper.
 *
 * All sound is generated via the Web Audio API — no assets to download.
 * A single AudioContext is lazily created after the first user gesture
 * (browsers block audio without one). Volume + on/off persist in
 * localStorage so returning users hear (or don't hear) it consistently.
 */

const STORAGE_KEY = "zu_audio";
let ctx = null;
let masterGain = null;
let droneNodes = null;
let armed = false;

function _load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { enabled: false, volume: 0.35 };   // OFF by default — user consent
}

function _save(cfg) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

let cfg = _load();

export function audioConfig() { return { ...cfg }; }

function _ensureCtx() {
  if (ctx) return ctx;
  try {
    // eslint-disable-next-line no-undef
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = cfg.enabled ? cfg.volume : 0;
    masterGain.connect(ctx.destination);
  } catch { /* browser blocked */ }
  return ctx;
}

/** Arm the context. Must be called from a user-gesture handler. */
export function armAudio() {
  _ensureCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  armed = true;
}

export function setEnabled(v) {
  cfg.enabled = !!v;
  _save(cfg);
  if (masterGain) masterGain.gain.value = cfg.enabled ? cfg.volume : 0;
  if (cfg.enabled) startDrone();
  else stopDrone();
}

export function setVolume(v) {
  cfg.volume = Math.max(0, Math.min(1, Number(v) || 0));
  _save(cfg);
  if (masterGain && cfg.enabled) masterGain.gain.value = cfg.volume;
}

// ------------------ DRONE ------------------
export function startDrone() {
  if (!cfg.enabled) return;
  _ensureCtx();
  if (!ctx || droneNodes) return;

  // Two detuned sawtooth oscillators through a low-pass = ominous sub drone
  const g = ctx.createGain();
  g.gain.value = 0.10;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 220;
  filter.Q.value = 3;
  const o1 = ctx.createOscillator();
  const o2 = ctx.createOscillator();
  o1.type = "sawtooth";
  o2.type = "sawtooth";
  o1.frequency.value = 55;   // A1
  o2.frequency.value = 55 * 1.006; // slight detune
  o1.connect(filter); o2.connect(filter);
  filter.connect(g); g.connect(masterGain);

  // Very slow lfo on the filter for movement
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.value = 0.06;
  lfoGain.gain.value = 80;
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);

  o1.start(); o2.start(); lfo.start();
  droneNodes = { o1, o2, lfo, g, filter, lfoGain };
}

export function stopDrone() {
  if (!droneNodes) return;
  try {
    const { o1, o2, lfo, g } = droneNodes;
    // Quick fade to avoid a click
    if (ctx) {
      const now = ctx.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(0, now + 0.25);
      setTimeout(() => {
        try { o1.stop(); o2.stop(); lfo.stop(); } catch { /* ignore */ }
      }, 300);
    }
  } catch { /* ignore */ }
  droneNodes = null;
}

// ------------------ SFX ------------------
function _sfx(fn) {
  if (!cfg.enabled) return;
  _ensureCtx();
  if (!ctx) return;
  try { fn(ctx, masterGain); } catch { /* ignore */ }
}

/** Sharp attacking punch — for ENGAGE. */
export function sfxEngage() {
  _sfx((ctx, master) => {
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(180, now);
    o.frequency.exponentialRampToValueAtTime(38, now + 0.42);
    g.gain.setValueAtTime(0.5, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
    o.connect(g); g.connect(master);
    o.start(now); o.stop(now + 0.5);
  });
}

/** Wet meaty thud — for VICTORY. */
export function sfxVictory() {
  _sfx((ctx, master) => {
    const now = ctx.currentTime;
    // Two low hits, half a beat apart
    for (const t of [0, 0.16]) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const f = ctx.createBiquadFilter();
      f.type = "lowpass"; f.frequency.value = 250;
      o.type = "square";
      o.frequency.setValueAtTime(90, now + t);
      o.frequency.exponentialRampToValueAtTime(30, now + t + 0.35);
      g.gain.setValueAtTime(0.45, now + t);
      g.gain.exponentialRampToValueAtTime(0.001, now + t + 0.35);
      o.connect(f); f.connect(g); g.connect(master);
      o.start(now + t); o.stop(now + t + 0.4);
    }
  });
}

/** Shrill screech — for DEFEAT. */
export function sfxDefeat() {
  _sfx((ctx, master) => {
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(880, now);
    o.frequency.exponentialRampToValueAtTime(140, now + 0.9);
    g.gain.setValueAtTime(0.001, now);
    g.gain.exponentialRampToValueAtTime(0.28, now + 0.08);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.9);
    o.connect(g); g.connect(master);
    o.start(now); o.stop(now + 1.0);
  });
}

/** Distant scream — for idle Command page ambience. */
export function sfxDistantScream() {
  _sfx((ctx, master) => {
    const now = ctx.currentTime;
    const noise = ctx.createBuffer(1, ctx.sampleRate * 1.5, ctx.sampleRate);
    const nd = noise.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = (Math.random() * 2 - 1) * 0.7;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 1400; bp.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, now);
    g.gain.exponentialRampToValueAtTime(0.12, now + 0.4);
    g.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
    src.connect(bp); bp.connect(g); g.connect(master);
    src.start(now); src.stop(now + 1.5);
  });
}

// Auto-boot drone on first page paint if the user previously enabled it.
// We still need a user gesture to un-suspend, so the first click after
// page-load will unlock things via armAudio() (called from the toggle).
export function isArmed() { return armed; }
export function isEnabled() { return cfg.enabled; }
